import { gql } from '@apollo/client'

/**
 * gets the string for `order` query
 * returns a single order, for the Order Detail screen
 * @internal
 */

/**
 * The `Order` selection the Order Detail screen and the in-webview order
 * confirmation both read.
 *
 * Shared rather than duplicated, the way `prefillCart` shares
 * `cartFieldsQuery`, and for a reason beyond DRY: the confirmation flow writes
 * the order it gets from `createOrder` / `confirmOrder` into the slice and
 * then replaces it with what a poll tick reads from this query. Two different
 * selections would mean the screen's content changed on the first tick — a
 * field the mutation did not ask for arriving from nowhere, or one it did
 * disappearing.
 *
 * Indented to sit under a field selection two levels deep, following
 * `cartFieldsQuery`. The interpolation is whitespace-only different from the
 * document that was inline here before, so `print(orderQuery)` — which the
 * Order Detail tests assert on — is unchanged.
 *
 * **`storeCode` (COM-889) needed no rollout gate, and that is a fact about
 * the field rather than about this selection.** Everything else here is
 * subject to the constraint the repo's `CLAUDE.md` states — a field this
 * string selects is requested by the `order` query, `createOrderMutation` and
 * `confirmOrderMutation` at once, and GraphQL validates a whole document
 * against the deployed schema before executing any of it, so a field the
 * running storefront does not declare breaks all three outright rather than
 * arriving `null`. `subtotal` (COM-739) is what that costs when skipped.
 * `storeCode` is exempt because it is not new: it sits on
 * `orderCommonTypeDefs` (`packages/graphql-schemas/src/types/commonFields.ts`
 * in storefront), which the user schema's `type Order` composes, and it is
 * present in every tag either environment could be running or roll back to —
 * checked against `v3.41.3`, `v3.41.5`, `v3.41.6` and `v3.42.0`, with
 * production on `v3.41.5` and staging on `v3.42.0` at the time of writing.
 * Do not read that as "adding a field here is fine"; read it as "this one was
 * checked, one tag at a time, and the next one has to be too".
 *
 * **`items { package { name } }` (COM-860) was checked the same way.** It is
 * what reorder from Order Detail sends back as `selectedPackage`; without it
 * every detail-sourced reorder went out in default packaging. `Order.items`
 * is `[CartProduct]` (`commonFields.ts`), `CartProduct.package` is
 * `PricedProductPackage` and its `name` is `String!` — identical in
 * `v3.41.3`–`v3.41.6`, `v3.42.0`–`v3.42.2` and `v3.43.0`, with production and
 * staging both on `v3.43.0` and `v3.42.2` the standing rollback. A
 * `storefront-user-stg` probe validated, with a made-up sibling field
 * rejected as `GRAPHQL_VALIDATION_FAILED` beside it. One runtime exposure
 * comes with it: `name` is non-null on the schema but not required by the
 * model, so a stored package with no name would put an error on all three
 * documents, which Apollo's default `errorPolicy` turns into a failed
 * response. It is not a new one — `getOrders` already selects this, and the
 * cart selects `package { name }` on the same `CartProduct` everywhere.
 *
 * **`checkoutRules { ruleType value }` (COM-1259) was checked the same way.**
 * The field mirrors `Session.checkoutRules`: `setCheckoutRuleSession` writes
 * the shopper's pick, and the order resolver copies the array forward at
 * create time (`setCheckoutRuleSession/mutation.ts` says so, and
 * `getSession/types.ts` repeats it on `SessionCheckoutRule`). `value` is the
 * same GraphQL `JSON` scalar on both ends, so a shape the session carries —
 * including the `key` and `attributes` the COM-1072 picker added — rides
 * through verbatim. `IOrderCheckoutRule` in `../order/types.ts` and
 * `getOrderPaymentPickLabel` in `utils/orderPaymentPickLabel.ts` are the two
 * consumers; the second feeds the `paymentMethodLabel` prop on both
 * `OrderConfirmationPanel` and `OrderDetailPanel` from
 * `@engyalo/delivery-ui-components@1.25.0`. A `storefront-user-stg` probe on
 * 2026-10-14 validated: a document selecting `order { id checkoutRules
 * { ruleType value } }` with a bogus storefront name returned a `404` from
 * the resolver's own `validateStorefront` middleware, and a sibling probe
 * selecting `__com1259NotAField` on `Order` returned
 * `GRAPHQL_VALIDATION_FAILED`. Production is on auth and could not be probed
 * from this session; `commerce-web/packages/core/src/graphql/getOrder.ts`
 * selects the same shape against the same production endpoint and ships
 * today, which is the standing cross-check. Re-run the probe against
 * production before merge with the standing recipe (same document, bogus
 * storefront name — a `404` is a pass, a `GRAPHQL_VALIDATION_FAILED` a
 * block). COM-1253 widens `SessionCheckoutRuleValue` with `attributes` on
 * the cinnamon side; that one does not reach `Order` and does not change
 * this gate.
 * @internal
 */
export const orderFieldsQuery: string = `
      id
      sequence
      status
      externalRefStatus
      createdAt
      subtotal
      total
      totalFee
      compensations
      discountsByPromotion
      customFields
      activeFees {
        total
        fee {
          fee
          threshold
          type
        }
      }
      paymentMethod
      checkoutRules {
        ruleType
        value
      }
      expectedDeliveryDate
      storeCode
      customer {
        address
        city
        state
        zipCode
        country
        region
      }
      items {
        sku
        name
        description
        quantity
        price
        unitPrice
        imageURL
        packageType
        unitsPerPackage
        package {
          name
        }
        discounts
        customFields
      }
`

/*
 * `sessionUid` is sent alongside `id` on every call, and that is a security
 * property rather than a style choice. This is the authoritative statement of
 * it; every other site that touches the argument points back here.
 *
 * The user API declares both arguments as optional
 * (`order(storefrontName: String!, sessionUid: ID, id: ID)`) and branches on
 * them. With both, the resolver reads `customerUid` off the session and adds
 * it to the filter, so the shopper can only ever read their own orders. With
 * `id` alone it takes the legacy contract, where the session middleware loads
 * the order first and validates the session recorded *on the order* rather
 * than the caller's — a supported shape, but one that applies no scoping to
 * whoever is asking. Dropping `sessionUid` here moves the screen onto it.
 *
 * The value that reaches that branch is an omitted or `undefined` argument,
 * not an empty string. `validateOrder` puts both ids through
 * `joi.string().hex().length(24)` before the resolver branches, so `''` and
 * `null` are rejected outright as a 400 — loud, and harmless. An omitted
 * argument passes validation untouched, and Apollo drops an `undefined`
 * variable from the request body, so `undefined` and omitted are the same
 * request on the wire. `state.defaultSlice.sessionId` is initialised
 * `undefined`, which is what makes this reachable from an ordinary render
 * rather than theoretical. Hence the guard in `useGetOrderDetail`, which is
 * the only way into `buildVariables`.
 *
 * Scoping is by customer rather than by session on purpose, server-side: an id
 * created in an earlier session still resolves for the same shopper.
 */
const orderQuery = gql`
  query Order($storefrontName: String!, $sessionUid: ID, $id: ID) {
    order(storefrontName: $storefrontName, sessionUid: $sessionUid, id: $id) {
      ${orderFieldsQuery}
    }
  }
`

export default orderQuery
